import { useState, useEffect } from "react";
import { Phone, MessageCircle, Menu, X, Truck, Clock } from "lucide-react";
import logoImage from "@/assets/ez-logo.jpeg";


export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "About Us", href: "#about" },
    { label: "Product Range", href: "#products" },
    { label: "Our Promise", href: "#promise" },
    { label: "Services", href: "#services" },
    { label: "Request Refill", href: "#refill" },
    { label: "Location", href: "#location" },
    { label: "Contact", href: "#contact" },
  ];

  const handleNavClick = (href) => {
    setIsOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* TOP UTILITY BAR */}
      <div className="bg-ez-teal text-white text-sm py-2 z-50 relative">
        <div className="ez-container flex justify-end items-center gap-6">
          <span className="hidden md:flex items-center gap-1.5 font-semibold">
            <Clock size={14} />
            Mon–Fri: 9AM–7PM &nbsp;|&nbsp; Sat: 9AM–2PM
          </span>
          <span className="hidden sm:flex items-center gap-1.5 font-semibold">
            <Truck size={14} />
            FREE DELIVERY
          </span>
          <a
            href="https://wa.me/12016742138"
            data-testid="whatsapp-topbar-link"
            className="flex items-center gap-1.5 hover:text-teal-100 transition-colors"
          >
            <MessageCircle size={14} />
            WhatsApp
          </a>
          <a
            href="tel:+18625201063"
            data-testid="phone-topbar-link"
            className="flex items-center gap-1.5 font-semibold hover:text-teal-100 transition-colors"
          >
            <Phone size={14} />
            +1 862-520-1063
          </a>
        </div>
      </div>

      {/* MAIN HEADER */}
      <header
        data-testid="main-navbar"
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-md"
            : "bg-white shadow-sm"
        }`}
      >
        <div className="ez-container py-4 flex items-center justify-between">
          {/* LOGO */}
          <a
            href="#"
            data-testid="navbar-logo"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="flex items-center gap-2"
          >
            <img
              src={logoImage}
              alt="EZ Care Pharmacy logo"
              className="w-12 h-12 object-contain"
            />
            <span className="text-xl font-bold text-ez-teal font-outfit leading-tight">
              EZ Care <span className="text-ez-text">Pharmacy</span>
            </span>
          </a>

          {/* NAV LINKS - Desktop */}
          <nav className="hidden lg:flex items-center gap-7 font-medium text-ez-muted font-jakarta">
            {navLinks.map((link) => (
              <button
                key={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => handleNavClick(link.href)}
                className="hover:text-ez-teal transition-colors duration-200 text-sm"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* RIGHT CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://wa.me/12016742138"
              data-testid="whatsapp-nav-btn"
              className="flex items-center gap-2 border border-green-500 text-green-600 hover:bg-green-50 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200"
            >
              <MessageCircle size={15} />
              WhatsApp
            </a>
            <a
              href="tel:+18625201063"
              data-testid="call-now-btn"
              className="ez-btn-primary text-sm py-2.5 px-5 flex items-center gap-2"
            >
              <Phone size={15} />
              Call Now
            </a>
          </div>

          {/* HAMBURGER - Mobile */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* MOBILE MENU */}
        {isOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden bg-white border-t border-gray-100 px-4 pb-5 pt-3"
          >
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="text-left py-2.5 px-3 rounded-lg text-ez-muted hover:text-ez-teal hover:bg-teal-50 transition-all font-medium"
                >
                  {link.label}
                </button>
              ))}
            </nav>
            <div className="mt-4 flex flex-col gap-2">
              <a
                href="https://wa.me/12016742138"
                className="flex items-center justify-center gap-2 border border-green-500 text-green-600 py-2.5 rounded-full font-semibold text-sm"
              >
                <MessageCircle size={15} />
                WhatsApp Us
              </a>
              <a
                href="tel:+18625201063"
                className="flex items-center justify-center gap-2 bg-ez-teal text-white py-2.5 rounded-full font-semibold text-sm"
              >
                <Phone size={15} />
                Call Now
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
